import { useState } from 'react'
import type { components } from '../../../api/generated/schema'
import { Button, Card, IconButton, Person, SectionLabel } from '../../../design/components'
import { useSceneCandidates } from '../queries'
import type { SceneCharacter, SceneCharactersSection } from '../types'
import type { SceneEditing } from '../useSceneEditing'
import styles from './play.module.css'

type Schema = components['schemas']

/**
 * The scene's cast, split into PCs and NPCs.
 *
 * **The split arrives from the server and the client uses it as given.** `characters.pcs`
 * and `characters.npcs` are computed server-side precisely so no client filters on the
 * role enum — the flat `all` list is there too, and this card deliberately does not build
 * the split out of it. If the two ever disagree, that is an API bug, not something to
 * paper over here.
 *
 * The spec's Combatants card wants a stress row per NPC. That is a full character sheet's
 * worth of state and it lives one tap away: each row links to its sheet, where Stage 5's
 * stress boxes are already editable and already write through the envelope. Duplicating
 * them here would be a second place for the same track to be wrong.
 */
export function CastCard({
  sceneId,
  characters,
  editing,
}: {
  sceneId: number
  characters: SceneCharactersSection | undefined
  editing: SceneEditing
}) {
  const [adding, setAdding] = useState(false)

  if (characters === undefined) return null

  const pcs = characters.pcs ?? []
  const npcs = characters.npcs ?? []
  const total = (characters.all ?? []).length

  return (
    <Card
      variant="harm"
      label="Cast"
      hint={total === 1 ? '1 present' : `${total} present`}
      action={
        editing.canEdit && !adding ? (
          <IconButton label="Add to the scene" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {total === 0 && !adding && <p className={styles.empty}>Nobody in this scene yet.</p>}

      {pcs.length > 0 && <CastGroup title="Player characters" entries={pcs} editing={editing} />}
      {npcs.length > 0 && (
        <CastGroup title="Non-player characters" entries={npcs} editing={editing} />
      )}

      {editing.canEdit && adding && (
        <AddToCastForm sceneId={sceneId} editing={editing} onClose={() => setAdding(false)} />
      )}
    </Card>
  )
}

function CastGroup({
  title,
  entries,
  editing,
}: {
  title: string
  entries: readonly SceneCharacter[]
  editing: SceneEditing
}) {
  return (
    <div className={styles.subBlock}>
      <SectionLabel tone="muted" micro>
        {title}
      </SectionLabel>

      <div className={styles.subList}>
        {entries.map((entry) => (
          // Stacked, not side by side: two buttons beside a name at 390px left the name
          // a few characters wide and wrapped it one letter per line.
          <div key={entry.id} className={styles.castRow}>
            <Person
              name={entry.name}
              portraitUrl={entry.portraitUrl}
              meta={String(entry.type)}
              characterId={entry.characterId}
            />

            {editing.canEdit && (
              <span className={styles.rowActions}>
                <Button
                  variant="secondary"
                  disabled={editing.setCharacterRole.isPending}
                  onClick={() =>
                    editing.setCharacterRole.mutate({
                      sceneCharacterId: entry.id,
                      role: entry.role === 'PC' ? 'NPC' : 'PC',
                    })
                  }
                >
                  {entry.role === 'PC' ? 'Make NPC' : 'Make PC'}
                </Button>
                <Button
                  variant="danger"
                  disabled={editing.removeCharacter.isPending}
                  onClick={() => editing.removeCharacter.mutate({ sceneCharacterId: entry.id })}
                >
                  Remove
                </Button>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Adds from the campaign roster.
 *
 * The candidate list is server-filtered — it excludes whoever is already in the scene —
 * so this never offers somebody the API would refuse and never subtracts one list from
 * another.
 */
function AddToCastForm({
  sceneId,
  editing,
  onClose,
}: {
  sceneId: number
  editing: SceneEditing
  onClose: () => void
}) {
  const [role, setRole] = useState<Schema['SceneCharacterType']>('NPC')
  const candidates = useSceneCandidates(sceneId)
  const found = candidates.data ?? []

  return (
    <div className={styles.inlineForm}>
      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Joining as
        </SectionLabel>
        <select
          className={styles.select}
          value={role}
          onChange={(event) => setRole(event.target.value as Schema['SceneCharacterType'])}
        >
          <option value="PC">Player character</option>
          <option value="NPC">Non-player character</option>
        </select>
      </label>

      {candidates.isPending && <p className={styles.note}>Loading the roster…</p>}

      {candidates.isSuccess && found.length === 0 && (
        <p className={styles.note}>Everyone on the campaign roster is already in this scene.</p>
      )}

      <div className={styles.pickerList}>
        {found.map((candidate) => (
          <button
            key={candidate.characterId}
            type="button"
            className={styles.pickerRow}
            disabled={editing.addCharacter.isPending}
            onClick={() =>
              editing.addCharacter.mutate(
                { characterId: candidate.characterId ?? 0, role },
                { onSuccess: onClose },
              )
            }
          >
            <Person
              name={candidate.name ?? ''}
              portraitUrl={candidate.portraitUrl}
              meta={String(candidate.type ?? '')}
            />
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
